/**
 * Tynn fetch-wrapper mot API-et. Kaster ApiError med serverens feilmelding.
 */

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`/api/v1${path}`, {
    method,
    credentials: 'include',
    headers: body !== undefined ? { 'Content-Type': 'application/json' } : undefined,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    let melding = `Feil (${res.status})`;
    try {
      const data = (await res.json()) as { error?: string; detaljer?: string[] };
      melding = data.error ?? melding;
      if (data.detaljer?.length) melding += `: ${data.detaljer.join(', ')}`;
    } catch {
      /* ignorer */
    }
    throw new ApiError(res.status, melding);
  }
  const text = await res.text();
  return (text ? JSON.parse(text) : null) as T;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  delete: <T>(path: string) => request<T>('DELETE', path),
};

// ---------- Delte typer ----------

export interface Me {
  id: string;
  navn: string;
  epost: string;
  /** null = innlogget, men ikke valgt tenant ennå. */
  rolle: 'sysadmin' | 'admin' | 'bruker' | null;
  tenantId: string | null;
  economyGroupId: string | null;
  tenantNavn: string | null;
  tenants: { id: string; navn: string; rolle: string }[];
}

export interface ChargerState {
  online: boolean;
  modus: 'FRAKOBLET' | 'VENTER' | 'LADER' | 'FULLFORT' | 'UKJENT';
  effektKw: number | null;
  oktKwh: number | null;
  hentetTs: string;
}

export interface Charger {
  id: string;
  navn: string;
  externalId: string;
  aktiv: boolean;
  personId: string | null;
  eierNavn: string | null;
  installationId: string;
  anleggNavn: string;
  prisomrade: string;
  effektKwDefault: string;
  sistState: ChargerState | null;
  sistStateTs: string | null;
}

export interface SpotPrice {
  prisomrade: string;
  ts: string;
  varighetMin: number;
  nokPerKwhEksMva: string;
}

export interface Person {
  id: string;
  navn: string;
  epost: string;
  telefon: string | null;
  rolle: string;
  economyGroupId: string | null;
  aktiv: boolean;
}

export interface EconomyGroup {
  id: string;
  navn: string;
}

export interface VendorAccount {
  id: string;
  vendor: string;
  navn: string;
  username: string | null;
  status: string;
  sisteSynk: string | null;
  sisteFeil: string | null;
}

export interface Installation {
  id: string;
  externalId: string;
  navn: string;
  adresse: string | null;
  prisomrade: string;
  gridTariffId: string | null;
  mvaSats: string;
  aktiv: boolean;
}

export interface GridTariff {
  id: string;
  navn: string;
  energileddDagOre: string;
  energileddNattOre: string;
  dagFraTime: number;
  dagTilTime: number;
  helgSomNatt: boolean;
  fastleddMnd: string;
}

export interface PowerDeal {
  id: string;
  personId: string | null;
  economyGroupId: string | null;
  type: string;
  navn: string | null;
  paslagOre: string;
  fastprisOre: string | null;
  fastbelopMnd: string;
  gyldigFra: string;
  gyldigTil: string | null;
}

export interface SessionRow {
  id: string;
  startTs: string;
  sluttTs: string | null;
  kwhTotal: string;
  kostStrom?: string | null;
  kostNett?: string | null;
  kostTotal: string | null;
  personId: string | null;
  personNavn: string | null;
  laderNavn?: string;
  manueltFlyttet: boolean;
  beregnetTs?: string | null;
}

export interface SummaryRow {
  key: string | null;
  navn: string;
  kwh: string;
  kostStrom: string;
  kostNett: string;
  kostTotal: string;
  antOkter: number;
  uberegnet: number;
}

// ---------- Formatering ----------

export function fmtKr(v: string | number | null | undefined): string {
  if (v === null || v === undefined || v === '') return '–';
  const n = typeof v === 'number' ? v : Number(v);
  return n.toLocaleString('nb-NO', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' kr';
}

export function fmtKwh(v: string | number | null | undefined): string {
  if (v === null || v === undefined || v === '') return '–';
  const n = typeof v === 'number' ? v : Number(v);
  return n.toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + ' kWh';
}

// Norsk datoformat: 01.01.2026 og 01.01.2026 09:52 (alltid Oslo-tid)
const datoFmt = new Intl.DateTimeFormat('nb-NO', {
  timeZone: 'Europe/Oslo',
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});
const klokkeFmt = new Intl.DateTimeFormat('nb-NO', {
  timeZone: 'Europe/Oslo',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

/** Tolker både ISO-tidspunkt og rene datoer (YYYY-MM-DD). */
function tilDato(v: string | Date | null | undefined): Date | null {
  if (!v) return null;
  // Ren dato forankres midt på dagen, ellers kan sonebytte flytte den et døgn
  const d =
    typeof v === 'string'
      ? new Date(/^\d{4}-\d{2}-\d{2}$/.test(v) ? `${v}T12:00:00Z` : v)
      : v;
  return Number.isNaN(d.getTime()) ? null : d;
}

/** 01.01.2026 */
export function fmtDato(v: string | Date | null | undefined): string {
  const d = tilDato(v);
  return d ? datoFmt.format(d) : '–';
}

/** 01.01.2026 09:52 */
export function fmtTid(v: string | Date | null | undefined): string {
  const d = tilDato(v);
  return d ? `${datoFmt.format(d)} ${klokkeFmt.format(d)}` : '–';
}

/** 07.2026 — månedsgruppering (serveren sender «2026-07»). */
export function fmtMaaned(yyyyMM: string): string {
  const m = /^(\d{4})-(\d{2})$/.exec(yyyyMM);
  return m ? `${m[2]}.${m[1]}` : yyyyMM;
}

export function isoDato(d: Date): string {
  return d.toLocaleDateString('sv-SE', { timeZone: 'Europe/Oslo' });
}
